/**
 * Admin — Multi-Store Orders Live Panel
 *
 * Lists active multi-orders with real-time polling.
 * Supports: view detail + audit log, force split, store multi-store config.
 */

import { useEffect, useState, useCallback } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import {
  RefreshCw, GitBranch, ChevronDown, ChevronUp, Clock,
  CheckCircle2, XCircle, AlertTriangle, Truck, Search,
} from "lucide-react";

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem("ayrshire_token");
  return token
    ? { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
    : { "Content-Type": "application/json" };
}

function pence(p: number | null | undefined) {
  if (p == null) return "—";
  return `£${(p / 100).toFixed(2)}`;
}

function relativeTime(iso: string) {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  awaiting_store_responses: { label: "Awaiting stores", color: "bg-yellow-100 text-yellow-800" },
  partially_accepted:       { label: "Partially accepted", color: "bg-orange-100 text-orange-800" },
  confirmed:                { label: "Confirmed", color: "bg-green-100 text-green-800" },
  searching_driver:         { label: "Searching driver", color: "bg-blue-100 text-blue-800" },
  driver_assigned:          { label: "Driver assigned", color: "bg-blue-100 text-blue-800" },
  coordinating_pickups:     { label: "Coordinating pickups", color: "bg-indigo-100 text-indigo-800" },
  first_pickup:             { label: "First pickup", color: "bg-teal-100 text-teal-800" },
  second_pickup:            { label: "Second pickup", color: "bg-teal-100 text-teal-800" },
  out_for_delivery:         { label: "Out for delivery", color: "bg-teal-100 text-teal-800" },
  split_delivery:           { label: "Split delivery", color: "bg-purple-100 text-purple-800" },
  completed:                { label: "Completed", color: "bg-green-100 text-green-800" },
  partially_completed:      { label: "Partially completed", color: "bg-green-100 text-green-800" },
  cancelled:                { label: "Cancelled", color: "bg-red-100 text-red-800" },
  failed:                   { label: "Failed", color: "bg-red-100 text-red-800" },
};

const CHILD_STATUS_LABELS: Record<string, { label: string; color: string }> = {
  pending:   { label: "Pending", color: "bg-yellow-100 text-yellow-800" },
  accepted:  { label: "Accepted", color: "bg-green-100 text-green-800" },
  ready:     { label: "Ready for pickup", color: "bg-teal-100 text-teal-800" },
  rejected:  { label: "Rejected", color: "bg-red-100 text-red-800" },
  decoupled: { label: "Decoupled", color: "bg-purple-100 text-purple-800" },
  completed: { label: "Completed", color: "bg-green-100 text-green-800" },
  cancelled: { label: "Cancelled", color: "bg-gray-100 text-gray-700" },
};

type ChildSummary = {
  id: number;
  orderId: number;
  storeId: number;
  storeName: string;
  role: "anchor" | "companion";
  childStatus: string;
  storePayoutPence: number | null;
  storeTransferId: string | null;
  refundedPence: number | null;
};

type MultiOrderRow = {
  id: number;
  customerId: number;
  customerName: string | null;
  customerEmail: string | null;
  status: string;
  deliveryAddress: string;
  splitDelivery: boolean;
  deliveryFeeTotalPence: number;
  refundedPence: number;
  driverId: number | null;
  createdAt: string;
  updatedAt: string;
  children: ChildSummary[];
};

type AuditEntry = {
  id: number;
  multiOrderId: number;
  event: string;
  detail: Record<string, unknown> | null;
  reason: string | null;
  createdAt: string;
};

type MultiOrderDetail = MultiOrderRow & {
  customer: { name: string; email: string } | null;
  auditLog: AuditEntry[];
};

export default function AdminMultiOrders() {
  const [orders, setOrders] = useState<MultiOrderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<MultiOrderDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [splitLoading, setSplitLoading] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);

  const fetchOrders = useCallback(async () => {
    try {
      const url = showCompleted
        ? "/api/admin/multi-orders?limit=100"
        : "/api/admin/multi-orders?limit=100";
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error("Failed to load");
      const data = await res.json() as MultiOrderRow[];
      setOrders(data);
      setError(null);
    } catch {
      setError("Failed to load multi-orders.");
    } finally {
      setLoading(false);
    }
  }, [showCompleted]);

  useEffect(() => {
    void fetchOrders();
    const interval = setInterval(() => void fetchOrders(), 15_000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  async function loadDetail(id: number) {
    if (expandedId === id) { setExpandedId(null); setDetail(null); return; }
    setExpandedId(id);
    setDetail(null);
    setDetailLoading(true);
    try {
      const res = await fetch(`/api/admin/multi-orders/${id}`, { headers: authHeaders() });
      const data = await res.json() as MultiOrderDetail;
      setDetail(data);
    } catch {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }

  async function handleForceSplit(id: number) {
    if (!confirm("Force split this multi-order into separate deliveries?")) return;
    setSplitLoading(true);
    try {
      const res = await fetch(`/api/admin/multi-orders/${id}/split`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ reason: "Admin-initiated split" }),
      });
      const data = await res.json() as { ok?: boolean; error?: string };
      if (!res.ok || !data.ok) {
        alert(data.error ?? "Failed to split order");
      } else {
        void fetchOrders();
        if (expandedId === id) void loadDetail(id);
      }
    } finally {
      setSplitLoading(false);
    }
  }

  const filtered = orders.filter(o => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      String(o.id).includes(q) ||
      (o.customerName ?? "").toLowerCase().includes(q) ||
      (o.customerEmail ?? "").toLowerCase().includes(q) ||
      o.deliveryAddress.toLowerCase().includes(q) ||
      o.children.some(c => c.storeName.toLowerCase().includes(q))
    );
  });

  return (
    <AdminLayout current="/admin/multi-orders">
      <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Multi-Store Orders</h1>
            <p className="text-sm text-gray-500 mt-1">Live panel · auto-refreshes every 15s</p>
          </div>
          <button
            onClick={() => void fetchOrders()}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium"
          >
            <RefreshCw size={15} />
            Refresh
          </button>
        </div>

        {/* Search + filter */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[12rem] max-w-md">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by order ID, customer, store, address…"
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showCompleted}
              onChange={e => setShowCompleted(e.target.checked)}
              className="rounded"
            />
            Include completed/cancelled
          </label>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">{error}</div>
        )}

        {/* Orders table */}
        {loading ? (
          <div className="text-center py-16 text-gray-400">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <GitBranch size={36} className="mx-auto mb-3 opacity-30" />
            <p className="font-medium">No active multi-store orders</p>
            <p className="text-sm mt-1">Orders will appear here as customers checkout</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(order => {
              const statusCfg = STATUS_LABELS[order.status] ?? { label: order.status, color: "bg-gray-100 text-gray-700" };
              const isExpanded = expandedId === order.id;
              const isTerminal = ["completed", "partially_completed", "cancelled", "failed"].includes(order.status);

              return (
                <div key={order.id} className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
                  {/* Row */}
                  <button
                    onClick={() => void loadDetail(order.id)}
                    className="w-full text-left px-5 py-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex items-start gap-4 flex-wrap">
                        <div>
                          <span className="font-mono text-sm font-bold text-gray-900">#{order.id}</span>
                          {order.splitDelivery && (
                            <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium">SPLIT</span>
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{order.customerName ?? "Guest"}</p>
                          <p className="text-xs text-gray-500">{order.customerEmail ?? ""}</p>
                        </div>
                        <div className="hidden sm:block">
                          <p className="text-xs text-gray-500 max-w-xs truncate">{order.deliveryAddress}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{relativeTime(order.createdAt)}</p>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {order.children.map(c => {
                            const cCfg = CHILD_STATUS_LABELS[c.childStatus] ?? { label: c.childStatus, color: "bg-gray-100 text-gray-700" };
                            return (
                              <span key={c.id} className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                                {c.storeName}: <span className={`font-medium ${cCfg.color.split(" ")[1]}`}>{cCfg.label}</span>
                              </span>
                            );
                          })}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusCfg.color}`}>
                          {statusCfg.label}
                        </span>
                        {isExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                      </div>
                    </div>
                  </button>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div className="border-t border-gray-100 bg-gray-50 p-5 space-y-5">
                      {detailLoading ? (
                        <div className="text-center py-8 text-gray-400 text-sm">Loading detail…</div>
                      ) : detail?.id === order.id ? (
                        <>
                          {/* Store children */}
                          <div>
                            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Child orders</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {detail.children.map(c => {
                                const cCfg = CHILD_STATUS_LABELS[(c as { childStatus: string }).childStatus] ?? { label: "", color: "bg-gray-100 text-gray-700" };
                                return (
                                  <div key={(c as { id: number }).id} className="bg-white rounded-lg border border-gray-200 p-4">
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="font-semibold text-sm text-gray-900">{(c as { storeName: string }).storeName}</span>
                                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cCfg.color}`}>{cCfg.label}</span>
                                    </div>
                                    <div className="space-y-1 text-xs text-gray-500">
                                      <div className="flex justify-between"><span>Role</span><span className="font-medium capitalize">{(c as { role: string }).role}</span></div>
                                      <div className="flex justify-between"><span>Order #{(c as { orderId: number }).orderId}</span><span>Payout: {pence((c as { storePayoutPence: number | null }).storePayoutPence)}</span></div>
                                      {(c as { storeTransferId: string | null }).storeTransferId && (
                                        <div className="flex justify-between"><span>Transfer</span><span className="font-mono text-xs">{(c as { storeTransferId: string }).storeTransferId}</span></div>
                                      )}
                                      {(c as { refundedPence: number | null }).refundedPence != null && (c as { refundedPence: number }).refundedPence > 0 && (
                                        <div className="flex justify-between text-red-600"><span>Refunded</span><span>{pence((c as { refundedPence: number }).refundedPence)}</span></div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          {/* Financials */}
                          <div className="bg-white rounded-lg border border-gray-200 p-4 text-sm">
                            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Financials</h3>
                            <div className="space-y-1.5">
                              <div className="flex justify-between text-gray-600">
                                <span>Combined delivery fee</span>
                                <span>{pence(detail.deliveryFeeTotalPence)}</span>
                              </div>
                              {detail.refundedPence > 0 && (
                                <div className="flex justify-between text-red-600">
                                  <span>Total refunded</span>
                                  <span>{pence(detail.refundedPence)}</span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Actions */}
                          {!isTerminal && !order.splitDelivery && (
                            <div>
                              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Actions</h3>
                              <button
                                onClick={() => void handleForceSplit(order.id)}
                                disabled={splitLoading}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-purple-300 text-purple-700 text-sm font-medium hover:bg-purple-50 disabled:opacity-50"
                              >
                                <GitBranch size={14} />
                                Force split delivery
                              </button>
                            </div>
                          )}

                          {/* Audit log */}
                          {detail.auditLog.length > 0 && (
                            <div>
                              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Audit log</h3>
                              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                                {detail.auditLog.map(entry => (
                                  <div key={entry.id} className="bg-white rounded border border-gray-100 px-3 py-2 text-xs">
                                    <div className="flex items-center justify-between gap-2 mb-1">
                                      <span className="font-mono font-semibold text-gray-800">{entry.event}</span>
                                      <span className="text-gray-400 shrink-0">{relativeTime(entry.createdAt)}</span>
                                    </div>
                                    {entry.reason && (
                                      <p className="text-orange-600 mb-1">Reason: {entry.reason}</p>
                                    )}
                                    {entry.detail && (
                                      <pre className="text-gray-500 text-xs overflow-x-auto whitespace-pre-wrap break-all">
                                        {JSON.stringify(entry.detail, null, 2)}
                                      </pre>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      ) : null}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
